import {
  isColorGradient,
  NavbarDropdown,
  NavbarItem,
  textColors,
  computeColor,
  textColorsFallback,
  fonts,
  computeFont,
} from '@typedream/core';
import {
  NavbarMenuItemButton,
  NavbarMenuItemLink,
  NavbarMenuItemProps,
} from '@typedream/components';
import { useContext, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { ChevronDown } from '@styled-icons/bootstrap';
import { shift, useFloating } from '@floating-ui/react-dom';
import useMergedRef from '@react-hook/merged-ref';
import { NavbarDropdownMenuEditor } from './NavbarDropdownMenuEditor';
import { useTPlateEditorRef } from '../../../plate';
import { useMenuItemDnd } from '../hooks/dnd';
import { NavbarDropdownOpenedMenuContext } from '../context/navbarDropdownOpenedMenu.context';

// ======================================================
// STYLED COMPONENT
// ======================================================
const NavbarMenuItemContainerEditor = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-grow: 0;
  align-items: stretch;
  width: fit-content;
  white-space: nowrap;
`;

const OpenDropdownTooltip = styled.div`
  position: absolute;
  padding: 0.5rem 1rem;
  border-radius: 200px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  max-width: fit-content;

  transition: all 0.15s ease-in;
  pointer-events: none;
`;
// ======================================================
// STYLED COMPONENT END
// ======================================================

type NavbarMenuItemElementEditorProps = Omit<NavbarMenuItemProps<NavbarItem>, 'onClick'> & {
  onClick: (ref: HTMLElement | null) => void;
};

const NavbarItemDropdownRoot = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-grow: 0;
  align-items: stretch;
  color: var(--text, inherit);
  font-weight: 500;
  line-height: 1.5;
  cursor: pointer;
  width: fit-content;
  white-space: nowrap;
  padding: 0.5rem 1rem;
`;

const GradientTextColor = css`
  background-clip: text !important;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
  -moz-background-clip: text;
  -moz-text-fill-color: transparent;
  -ms-background-clip: text;
  -ms-text-fill-color: transparent;
`;

const DivGradientTextColor = styled.div<{ isGradient: boolean }>`
  ${(props) => props.isGradient && GradientTextColor}
`;

function NavbarMenuItemDropdownEditor(props: NavbarMenuItemProps<NavbarDropdown>) {
  const { item, onClick } = props;
  const { openedId, setOpenedId } = useContext(NavbarDropdownOpenedMenuContext);
  const [isHovering, setIsHovering] = useState(false);
  const editor = useTPlateEditorRef();
  const isGradientColor = isColorGradient(item.color);
  const isEmpty = !item.title;

  const { x, y, reference, floating, strategy } = useFloating<HTMLDivElement>({
    middleware: [shift()],
  });

  const { drag: dragRef, drop: dropRef } = useMenuItemDnd(props);
  const dndRef = useMergedRef(dropRef, dragRef, reference);

  return (
    <NavbarItemDropdownRoot
      onMouseEnter={() => {
        setIsHovering(true);
        editor.emitter.emit('toolbar:toggle-navbar-container-inside-dropdown', { isInside: true });
      }}
      onMouseLeave={() => {
        setIsHovering(false);
        editor.emitter.emit('toolbar:toggle-navbar-container-inside-dropdown', { isInside: false });
      }}
      contentEditable={false}
    >
      <DivGradientTextColor
        ref={dndRef}
        isGradient={isGradientColor}
        className="todo-onclick-open-menu"
        tabIndex={0}
        role="button"
        onKeyDown={() => {}}
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: isGradientColor && item.color ? { ...textColors }[item.color] : undefined,
          color: isEmpty
            ? 'var(--text-light)'
            : computeColor(item.color, { ...textColorsFallback }),
          fontFamily: computeFont(item.fontFamily),
        }}
        onClick={onClick}
      >
        {item.title}
        <ChevronDown
          size={15}
          style={{
            marginLeft: '1rem',
            transition: 'transform 0.3s',
            transform: openedId === item.menu.id ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (!(openedId === item.menu.id)) {
              editor.emitter.emit('toolbar:toggle-navbar-container-inside-dropdown', {
                isInside: true,
              });
              setOpenedId(item.menu.id);
            } else {
              editor.emitter.emit('toolbar:toggle-navbar-container-inside-dropdown', {
                isInside: false,
              });
              setOpenedId(null);
            }
          }}
        />
      </DivGradientTextColor>

      <NavbarDropdownMenuEditor {...props} show={openedId === item.menu.id} />
      <OpenDropdownTooltip
        role="button"
        ref={floating}
        style={{
          position: strategy,
          opacity: !(openedId === item.menu.id) && isHovering ? 1 : 0,
          top: 0,
          left: 0,
          transform: `translate(${x}px, ${y}px)`,
        }}
      >
        Click dropdown icon to open the menu
      </OpenDropdownTooltip>
    </NavbarItemDropdownRoot>
  );
}

// Modification of NavbarMenuItemElement from @typedream/components.
// This component enable us to:
// 1. attach refs to render `MenuItemOverlayToolbar`
// 2. pass custom `onLeave` and `onMouse` enter in the `NavbarMenuItemDropdown`
export function NavbarMenuItemElementEditor(props: NavbarMenuItemElementEditorProps) {
  const { onClick, item, ...restProps } = props;
  const ref = useRef(null);
  const onClickElement: React.MouseEventHandler = (e) => {
    e.preventDefault();
    onClick(ref.current);
  };

  const { drop: dropRef, drag: dragRef } = useMenuItemDnd(props);

  const mergedRef = useMergedRef(ref, dragRef, dropRef);

  const content = () => {
    switch (item.type) {
      case 'button':
        return <NavbarMenuItemButton item={item} onClick={onClickElement} {...restProps} />;

      case 'link':
        return <NavbarMenuItemLink editable item={item} onClick={onClickElement} {...restProps} />;

      case 'dropdown':
        return <NavbarMenuItemDropdownEditor item={item} onClick={onClickElement} {...restProps} />;

      case 'web3_button':
      default:
        return <span>Unknown Type</span>;
    }
  };

  return (
    <NavbarMenuItemContainerEditor
      // for dropdown, we need to attach the ref inside the
      // component NavbarMenuItemDropdownEditor
      ref={item.type !== 'dropdown' ? mergedRef : ref}
    >
      {content()}
    </NavbarMenuItemContainerEditor>
  );
}
